import React, { useEffect, useState } from "react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { PhoneIcon } from "@heroicons/react/24/solid";
import { FaMailBulk, FaHeadset, FaQuestionCircle, FaRegLightbulb } from "react-icons/fa";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const HelpCenter = () => {
    const [openFAQ, setOpenFAQ] = useState(null);

    const faqs = [
        {
            question: "How do I create an account?",
            answer:
                "Click on 'Sign Up' in the top right corner, fill out the required information, and verify your email address.",
        },
        {
            question: "How can I reset my password?",
            answer:
                "Go to the login page and click on 'Forgot Password'. Enter your registered email, and we'll send you a reset link.",
        },
        {
            question: "How do I apply for a job?",
            answer:
                "Browse jobs, click on the desired job listing, and press 'Apply Now'. Ensure your profile and resume are up to date.",
        },
        {
            question: "What file types are accepted for resume upload?",
            answer:
                "We accept PDF, DOCX, and DOC file formats. Maximum file size is 5MB.",
        },
    ];

    const openGmailComposer = () => {
        const gmailComposeUrl =
            "https://mail.google.com/mail/?view=cm&fs=1&to=support@jobportal.com";
        window.open(gmailComposeUrl, "_blank");
    };

    const toggleFAQ = (index) => {
        setOpenFAQ(openFAQ === index ? null : index);
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="">
            <div className="bg-mainBg p-3">
                <div className="min-h-screen bg-gradient-to-br  py-12 px-4 sm:px-6 lg:px-8">
                    <div className=" mx-auto">
                        {/* Header with Animated Icon */}
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7 }}
                            className="text-center mb-8"
                        >
                            <div className="inline-block p-3 rounded-full bg-blue-100 mb-4">
                                <FaHeadset className="text-blue-600 w-12 h-12" />
                            </div>
                            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-700 to-indigo-600 bg-clip-text text-transparent mb-3">
                                Help & Support Center
                            </h1>
                            <p className="text-lg text-blue-700 font-medium max-w-2xl mx-auto">
                                We're here to help you succeed on your job search journey
                            </p>
                        </motion.div>

                        {/* Main Content */}
                        <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-blue-100">
                            {/* Support Channels */}
                            <div className="grid md:grid-cols-2 gap-6 p-6 md:p-8 bg-gradient-to-br from-blue-50 to-white">
                                <motion.div
                                    whileHover={{ scale: 1.02 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                    className="bg-white p-6 rounded-xl text-center shadow-md border border-blue-100"
                                >
                                    <div className="rounded-full bg-blue-100 p-3 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                        <PhoneIcon className="text-blue-600 w-8 h-8" />
                                    </div>
                                    <h3 className="text-xl font-bold text-blue-900 mb-2">
                                        Direct Support Line
                                    </h3>
                                    <p className="text-blue-800 text-base mb-4 font-semibold">
                                        +91 8827067029
                                    </p>
                                    <p className="text-gray-600 mb-4 text-sm">
                                        Available Mon-Fri, 9am-6pm EST
                                    </p>
                                    <a
                                        href="tel:+91 8827067029"
                                        className="bg-blue-600 text-white px-6 py-2 rounded-full text-base font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center mx-auto w-max"
                                    >
                                        <PhoneIcon className="w-4 h-4 mr-2" />
                                        Call Support
                                    </a>
                                </motion.div>

                                <motion.div
                                    whileHover={{ scale: 1.02 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                    className="bg-white p-6 rounded-xl text-center shadow-md border border-blue-100"
                                >
                                    <div className="rounded-full bg-blue-100 p-3 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                        <FaMailBulk className="text-blue-600 w-8 h-8" />
                                    </div>
                                    <h3 className="text-xl font-bold text-blue-900 mb-2">
                                        Email Support
                                    </h3>
                                    <p className="text-blue-800 text-base mb-4 font-semibold">
                                        support@indiahirings.com
                                    </p>
                                    <p className="text-gray-600 mb-4 text-sm">
                                        We typically respond within 24 hours
                                    </p>
                                    <button
                                        onClick={openGmailComposer}
                                        className="bg-blue-600 text-white px-6 py-2 rounded-full text-base font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center mx-auto w-max"
                                    >
                                        <FaMailBulk className="mr-2 w-4 h-4" />
                                        Compose Email
                                    </button>
                                </motion.div>
                            </div>

                            {/* FAQ Section */}
                            <div className="p-6 md:p-8">
                                <div className="flex items-center justify-center mb-6">
                                    <FaQuestionCircle className="text-blue-600 w-6 h-6 mr-2" />
                                    <h2 className="text-2xl font-bold text-blue-900">
                                        Frequently Asked Questions
                                    </h2>
                                </div>

                                <div className="space-y-4">
                                    {faqs.map((faq, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.1 }}
                                            className="bg-white rounded-lg shadow-sm border border-blue-100 overflow-hidden"
                                        >
                                            <button
                                                onClick={() => toggleFAQ(index)}
                                                className="w-full flex justify-between items-center text-left p-4 group"
                                            >
                                                <div className="flex items-center">
                                                    <FaRegLightbulb className="text-blue-500 mr-2 flex-shrink-0 w-5 h-5" />
                                                    <span className="text-base font-semibold text-blue-800 group-hover:text-blue-600 transition-colors">
                                                        {faq.question}
                                                    </span>
                                                </div>
                                                <ChevronDownIcon
                                                    className={`text-blue-600 w-5 h-5 transition-transform duration-300 ${openFAQ === index ? "transform rotate-180" : ""
                                                        }`}
                                                />
                                            </button>
                                            {openFAQ === index && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: "auto" }}
                                                    transition={{ duration: 0.3 }}
                                                    className="bg-blue-50 border-t border-blue-100"
                                                >
                                                    <p className="p-4 text-blue-700 text-sm">
                                                        {faq.answer}
                                                    </p>
                                                </motion.div>
                                            )}
                                        </motion.div>
                                    ))}
                                </div>
                            </div>

                            {/* Additional Help Section */}
                            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 md:p-8 text-white text-center">
                                <h3 className="text-xl font-bold mb-3">Still Need Help?</h3>
                                <p className="text-blue-100 mb-4 max-w-2xl mx-auto text-sm">
                                    Our knowledgeable support team is available to assist you with any questions or issues you may encounter during your job search journey.
                                </p>
                                <div className="flex flex-wrap gap-3 justify-center">
                                    <Link to={"/contact-us"}>

                                        <button className="bg-white text-blue-700 px-4 py-2 rounded-full text-base font-semibold hover:bg-blue-50 transition-colors">
                                            Contact Us
                                        </button>
                                    </Link>

                                </div>
                            </div>
                        </div>


                    </div>
                </div>
            </div>
        </div>
    );
};

export default HelpCenter;